// API client para apps/deals/ (Django backend, NABU-67).
// Mapea los endpoints admin protegidos por JWT:
//   GET    /admin/deals/stages          GET    /admin/deals/forecast
//   POST   /admin/deals/stages          PATCH  /admin/deals/stages/{id}
//   DELETE /admin/deals/stages/{id}
//   GET    /admin/deals/                POST   /admin/deals/
//   GET    /admin/deals/{uuid}          PATCH  /admin/deals/{uuid}
//   DELETE /admin/deals/{uuid}          PATCH  /admin/deals/{uuid}/stage
//   GET    /admin/deals/follow-ups      (semáforo: cliente + próximo contacto)
//   GET    /admin/deals/{uuid}/contacts POST   /admin/deals/{uuid}/contact
//
// Auth: JWT (Authorization: Bearer ...). Multi-tenant resuelto por
// el backend con `request.jwt_org_id`.

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  ContactIn,
  ContactRow,
  Deal,
  DealCreateIn,
  DealListFilters,
  DealStage,
  DealStageCreateIn,
  DealStageMoveIn,
  DealStageUpdateIn,
  DealUpdateIn,
  FollowUpRow,
  ForecastReport,
} from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/deals';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  return headers;
}

export class DealsApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'DealsApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: string }).detail || `HTTP ${res.status}`;
    throw new DealsApiError(
      typeof detail === 'string' ? detail : JSON.stringify(detail),
      res.status,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

function buildQuery(filters: DealListFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.stage_id !== undefined) params.set('stage_id', String(filters.stage_id));
  if (filters.owner_user_id !== undefined) params.set('owner_user_id', String(filters.owner_user_id));
  if (filters.min_value !== undefined) params.set('min_value', String(filters.min_value));
  if (filters.max_value !== undefined) params.set('max_value', String(filters.max_value));
  if (filters.close_from) params.set('close_from', filters.close_from);
  if (filters.close_to) params.set('close_to', filters.close_to);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const dealsApi = {
  // ── Stages ────────────────────────────────────────────
  // GET siembra (lazy, idempotente) el embudo canónico si la org no tiene etapas.
  listStages: (): Promise<DealStage[]> => request<DealStage[]>(`${BASE}/stages`),

  // Configuración del tablero: solo se ofrece a admins en la UI.
  createStage: (data: DealStageCreateIn): Promise<DealStage> =>
    request<DealStage>(`${BASE}/stages`, { method: 'POST', body: JSON.stringify(data) }),

  updateStage: (id: number, data: DealStageUpdateIn): Promise<DealStage> =>
    request<DealStage>(`${BASE}/stages/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // ── Deals ─────────────────────────────────────────────
  list: (filters: DealListFilters = {}): Promise<Deal[]> =>
    request<Deal[]>(`${BASE}/${buildQuery(filters)}`),

  get: (id: string): Promise<Deal> => request<Deal>(`${BASE}/${id}`),

  create: (data: DealCreateIn): Promise<Deal> =>
    request<Deal>(`${BASE}/`, { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: DealUpdateIn): Promise<Deal> =>
    request<Deal>(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  remove: (id: string): Promise<void> =>
    request<void>(`${BASE}/${id}`, { method: 'DELETE' }),

  // Drag-drop kanban — endpoint dedicado para que el BI distinga la transición.
  moveStage: (id: string, data: DealStageMoveIn): Promise<Deal> =>
    request<Deal>(`${BASE}/${id}/stage`, { method: 'PATCH', body: JSON.stringify(data) }),

  // ── Forecast ──────────────────────────────────────────
  getForecast: (): Promise<ForecastReport> => request<ForecastReport>(`${BASE}/forecast`),

  // ── Seguimiento (semáforo RentalOS F2) ────────────────
  // Deals ABIERTOS con nombre del cliente (lead) y próximo contacto ya calculados.
  listFollowUps: (limit = 500): Promise<FollowUpRow[]> =>
    request<FollowUpRow[]>(`${BASE}/follow-ups?limit=${limit}`),

  listContacts: (id: string): Promise<ContactRow[]> =>
    request<ContactRow[]>(`${BASE}/${id}/contacts`),

  registerContact: (id: string, data: ContactIn): Promise<ContactRow> =>
    request<ContactRow>(`${BASE}/${id}/contact`, { method: 'POST', body: JSON.stringify(data) }),
};
